import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { DotsThree } from '@phosphor-icons/react';
import './ProjectsPage.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import PaywallModal from './components/PaywallModal';
import { listProjects, createBlankProject, setupBilling, getBillingUsage, confirmCreditPurchase, deleteProject } from './lib/api';
import { trackMetaEvent } from './lib/meta';
import LandingPage from './LandingPage';
import { useProjectsStore, useProjectsData, fetchBillingIfStale } from './stores';
import { formatProjectCostCents } from './utils/currency';
import { getCreatorInitials } from './utils/projectCreator';

const ITEMS_PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 300;
const PROCESSING_DURATION_MS = 11 * 60 * 1000; // 11 minutes for full progress

// Calculate processing progress based on time since creation
const getProcessingProgress = (createdAt) => {
  if (!createdAt) return 0;
  const elapsed = Date.now() - new Date(createdAt).getTime();
  // Clamp between 0 and 99 (never show 100% while still processing)
  return Math.min(99, (elapsed / PROCESSING_DURATION_MS) * 100);
};

function ProjectsListSkeleton() {
  return (
    <div className="projects-list projects-list-skeleton" aria-label="Loading projects" aria-busy="true">
      <div className="projects-list-header" aria-hidden="true">
        <span>Project</span>
        <span>Creator</span>
        <span>Status</span>
        <span>Created</span>
        <span>Credits used</span>
        <span />
      </div>
      {Array.from({ length: 6 }, (_, index) => (
        <div className="projects-row projects-skeleton-row" key={index} aria-hidden="true">
          <span className="projects-skeleton-line projects-skeleton-project" />
          <span className="projects-skeleton-line projects-skeleton-creator" />
          <span className="projects-skeleton-line projects-skeleton-status" />
          <span className="projects-skeleton-line projects-skeleton-date" />
          <span className="projects-skeleton-line projects-skeleton-cost" />
          <span />
        </div>
      ))}
    </div>
  );
}

function ProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuthContext();

  const { items: cachedProjects, hasCache: hasCachedDefault } = useProjectsData(user?.id);
  const setStoreProjects = useProjectsStore((s) => s.setProjects);

  // Seed the initial render from the cached default view so navigation/refresh feels instant.
  const [projects, setProjects] = useState(() => (hasCachedDefault ? cachedProjects : []));
  const [isLoading, setIsLoading] = useState(!hasCachedDefault);
  const [error, setError] = useState(null);
  const [showCreationDropdown, setShowCreationDropdown] = useState(false);
  const [isCreatingBlank, setIsCreatingBlank] = useState(false);
  const [showLanding, setShowLanding] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [billingUsage, setBillingUsage] = useState(null);
  const [checkingBilling, setCheckingBilling] = useState(false);
  const hasCheckedBilling = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  
  // Debounce ref for search
  const searchTimeoutRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Highlight state from navigation
  const highlightProjectId = location.state?.highlightProjectId || null;

  // Delete confirmation state
  const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  
  // Progress tick state for live updating progress bars
  const [progressTick, setProgressTick] = useState(0);

  const isDefaultView = currentPage === 1 && statusFilter === 'all' && !debouncedSearch;

  const isProjectLimitError = useCallback((error) => {
    if (!error) return false;
    return error.code === 'FREE_PROJECT_LIMIT_REACHED' || (
      error.status === 402 && /free plan includes one project/i.test(error.diagnosticMessage || '')
    );
  }, []);

  const fetchBillingStatus = useCallback(async () => {
    if (checkingBilling) return;
    setCheckingBilling(true);
    try {
      const usage = await getBillingUsage();
      setBillingUsage(usage);
    } catch (_) {
      // Ignore usage failures; paywall can still render without usage stats.
    } finally {
      hasCheckedBilling.current = true;
      setCheckingBilling(false);
    }
  }, [checkingBilling]);

  const openPaywall = useCallback(async () => {
    if (!hasCheckedBilling.current) {
      await fetchBillingStatus();
    }
    setPaywallOpen(true);
  }, [fetchBillingStatus]);

  const handleCheckout = useCallback(async () => {
    try {
      const currentUrl = window.location.href;
      const response = await setupBilling(
        `${currentUrl}?billing=success`,
        `${currentUrl}?billing=cancelled`
      );

      if (response?.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (err) {
      setError('Unable to start checkout. Please try again.');
    }
  }, []);

  useEffect(() => {
    const handleReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const billingStatus = urlParams.get('billing');
      const creditsStatus = urlParams.get('credits');
      const sessionId = urlParams.get('session_id');

      if (!billingStatus && !creditsStatus) return;

      if (billingStatus === 'success' || creditsStatus === 'success') {
        if (creditsStatus === 'success' && sessionId) {
          try {
            const result = await confirmCreditPurchase(sessionId);
            const purchaseValue = result?.creditedCents;
            if (purchaseValue) {
              trackMetaEvent('Purchase', {
                value: purchaseValue / 100,
                currency: 'USD',
                content_type: 'credits',
                order_id: sessionId,
                num_items: 1,
              }, `purchase_${sessionId}`);
            }
          } catch (err) {
            console.error('Failed to confirm credit purchase:', err);
          }
        }
        setPaywallOpen(false);
        hasCheckedBilling.current = false;
        fetchBillingStatus();
        // Force-refresh the shared billing cache so the sidebar credit balance updates.
        fetchBillingIfStale({ userId: user?.id, force: true });
      }

      window.history.replaceState({}, '', window.location.pathname);
    };

    handleReturn();
  }, [fetchBillingStatus]);

  // Fetch projects with pagination. Default view (page 1, no filters) also syncs the SWR cache.
  const fetchProjects = useCallback(async (page, status, search) => {
    if (!user || loading) return;

    const viewIsDefault = page === 1 && status === 'all' && !search;
    const hasDataAlready = viewIsDefault && hasCachedDefault;

    try {
      if (!hasDataAlready) setIsLoading(true);
      setError(null);
      const response = await listProjects({
        page,
        limit: ITEMS_PER_PAGE,
        status: status === 'all' ? null : status,
        search: search || null,
      });
      setProjects(response.projects || []);
      setPagination(response.pagination || null);
      if (viewIsDefault) setStoreProjects(response.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, [user, loading, setStoreProjects, hasCachedDefault]);

  // Initial fetch and fetch on filter/page changes
  useEffect(() => {
    fetchProjects(currentPage, statusFilter, debouncedSearch);
  }, [currentPage, statusFilter, debouncedSearch, fetchProjects]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to page 1 when search changes
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, debouncedSearch]);

  // Reset to page 1 when status filter changes
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  // Progress tick with random delays for live-updating progress bars on processing projects
  useEffect(() => {
    const hasProcessingProjects = projects.some((p) => p.status === 'processing');
    if (!hasProcessingProjects) return;

    let timeoutId;
    const scheduleNextTick = () => {
      // Random delay between 800ms and 2500ms
      const delay = 800 + Math.random() * 1700;
      timeoutId = setTimeout(() => {
        setProgressTick((t) => t + 1);
        scheduleNextTick();
      }, delay);
    };
    scheduleNextTick();

    return () => clearTimeout(timeoutId);
  }, [projects]);

  // Page navigation
  const goToPage = (page) => {
    if (page >= 1 && page <= (pagination?.totalPages || 1)) {
      setCurrentPage(page);
      // Scroll to top of projects list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCreateBlankProject = async () => {
    if (!user) {
      navigate('/');
      return;
    }

    try {
      setIsCreatingBlank(true);
      const projectId = Math.random().toString(36).substring(2, 15);
      
      // Create blank project
      await createBlankProject(projectId);
      
      // Navigate to project page
      navigate(`/project/${projectId}`, {
        state: {
          promptText: '',
          attachments: [],
          isBlankProject: true,
        },
      });
      
      // Close modal
      setShowCreationDropdown(false);
    } catch (err) {
      console.error('Failed to create blank project:', err);
      if (isProjectLimitError(err)) {
        setShowCreationDropdown(false);
        setError(null);
        await openPaywall();
      } else {
        setError('Failed to create blank project');
      }
      setIsCreatingBlank(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      setIsDeletingProject(true);
      await deleteProject(projectId);
      setConfirmDeleteProjectId(null);
      // Remove from local state immediately
      setProjects((prev) => {
        const next = prev.filter((p) => p.projectId !== projectId);
        if (isDefaultView) setStoreProjects(next);
        return next;
      });
      // Update pagination count
      setPagination((prev) => prev ? { ...prev, totalCount: prev.totalCount - 1 } : prev);
    } catch (err) {
      setError('Failed to delete project');
      setConfirmDeleteProjectId(null);
    } finally {
      setIsDeletingProject(false);
    }
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      completed: { label: 'Completed', className: 'status-completed' },
      processing: { label: 'Processing', className: 'status-processing' },
      failed: { label: 'Failed', className: 'status-failed' },
    };
    
    const statusInfo = statusMap[status] || { label: status, className: 'status-unknown' };
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (!pagination) return [];
    
    const { totalPages } = pagination;
    const pages = [];
    const maxPagesToShow = 7;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, current and surrounding pages
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      pages.push(1);
      
      if (startPage > 2) {
        pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Check if we have any projects at all (for empty state)
  const hasNoProjectsAtAll = !isLoading && pagination?.totalCount === 0 && !debouncedSearch && statusFilter === 'all';

  return (
    <AuthenticatedLayout>
      <div className="projects-content">
        <div className="projects-header-section">
          <div className="projects-header-copy">
            <h1 className="projects-title">My Projects</h1>
            <p className="projects-subtitle">Create, organize, and manage all your video projects.</p>
          </div>
          {/* Creation Method Dropdown Menu */}
          <DropdownMenu.Root open={showCreationDropdown} onOpenChange={setShowCreationDropdown}>
            <DropdownMenu.Trigger asChild>
              <button
                className="projects-new-button"
                onClick={() => setShowCreationDropdown(!showCreationDropdown)}
                disabled={isCreatingBlank}
              >
                {isCreatingBlank ? 'Creating...' : '+ New Project'}
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content className="projects-dropdown-content" sideOffset={5}>
                <DropdownMenu.Item
                  className="projects-dropdown-item"
                  onClick={() => {
                    setShowCreationDropdown(false);
                    navigate('/');
                  }}
                >
                  <div className="projects-dropdown-label">
                    <div className="projects-dropdown-title">From a Prompt</div>
                    <div className="projects-dropdown-desc">Use AI to generate from text or files</div>
                  </div>
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  className="projects-dropdown-item"
                  onClick={handleCreateBlankProject}
                >
                  <div className="projects-dropdown-label">
                    <div className="projects-dropdown-title">From Scratch</div>
                    <div className="projects-dropdown-desc">Start with a blank project</div>
                  </div>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        {error && (
          <div className="projects-error">
            {error}
          </div>
        )}

        {isLoading && projects.length === 0 ? (
          <ProjectsListSkeleton />
        ) : hasNoProjectsAtAll ? (
          <div className="projects-empty-state">
            <h2>No projects yet</h2>
            <p>Create your first project to get started</p>
            <button
              className="projects-new-button projects-new-button-large"
              onClick={() => setShowCreationDropdown(true)}
              disabled={isCreatingBlank}
            >
              {isCreatingBlank ? 'Creating...' : '+ Create New Project'}
            </button>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="projects-filters">
              <div className="projects-search">
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="projects-search-input"
                />
              </div>
              <div className="projects-status-filter">
                <div className="projects-filter-tabs">
                  <button
                    className={`projects-filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('all')}
                  >
                    All
                  </button>
                  <button
                    className={`projects-filter-tab ${statusFilter === 'completed' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('completed')}
                  >
                    Completed
                  </button>
                  <button
                    className={`projects-filter-tab ${statusFilter === 'processing' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('processing')}
                  >
                    Processing
                  </button>
                  <button
                    className={`projects-filter-tab ${statusFilter === 'failed' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('failed')}
                  >
                    Failed
                  </button>
                </div>
              </div>
            </div>

            {/* Projects list */}
            {projects.length === 0 ? (
              <div className="projects-empty-state">
                <h2>No projects found</h2>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className={`projects-list${isLoading ? ' is-loading' : ''}`} aria-busy={isLoading}>
                  <div className="projects-list-header" aria-hidden="true">
                    <span>Project</span>
                    <span>Creator</span>
                    <span>Status</span>
                    <span>Created</span>
                    <span>Credits used</span>
                    <span />
                  </div>
                  {projects.map((project) => {
                    const isHighlighted = highlightProjectId === project.projectId;
                    const isProcessing = project.status === 'processing';
                    // eslint-disable-next-line no-unused-vars
                    const _ = progressTick; // Force re-render on tick
                    const progress = isProcessing ? getProcessingProgress(project.createdAt) : 0;
                    const projectCost = formatProjectCostCents(project.totalCostCents);
                    const creatorName = project.creatorName || 'Unknown creator';

                    return (
                      <ContextMenu.Root key={project.projectId}>
                        <ContextMenu.Trigger asChild>
                          <div
                            className={`projects-row${isHighlighted ? ' is-highlighted' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              if (event.target.closest('button')) return;
                              navigate(`/project/${project.projectId}`);
                            }}
                            onKeyDown={(event) => {
                              if (event.target !== event.currentTarget) return;
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                navigate(`/project/${project.projectId}`);
                              }
                            }}
                            aria-label={`Open ${project.name}`}
                          >
                            <div className="projects-row-project">
                              <h3 className="projects-row-title">{project.name}</h3>
                              <span className="projects-row-updated">Edited {formatRelativeDate(project.updatedAt)}</span>
                              {isProcessing && (
                                <div
                                  className="projects-row-progress"
                                  role="progressbar"
                                  aria-label={`${project.name} processing progress`}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                  aria-valuenow={Math.round(progress)}
                                >
                                  <div className="projects-row-progress-track">
                                    <div
                                      className="projects-row-progress-fill"
                                      style={{ width: `${progress}%` }}
                                    />
                                  </div>
                                  <span>{Math.round(progress)}%</span>
                                </div>
                              )}
                            </div>

                            <div className="projects-row-creator" title={creatorName}>
                              <span className="projects-mobile-label">Creator</span>
                              <span className="projects-creator-avatar" aria-hidden="true">
                                {getCreatorInitials(creatorName)}
                              </span>
                              <span className="projects-creator-name">{creatorName}</span>
                            </div>

                            <div className="projects-row-status">
                              <span className="projects-mobile-label">Status</span>
                              {getStatusBadge(project.status)}
                            </div>

                            <div className="projects-row-date">
                              <span className="projects-mobile-label">Created</span>
                              <span>{formatRelativeDate(project.createdAt)}</span>
                            </div>

                            <div className="projects-row-cost" aria-label={`Credits used: ${projectCost}`}>
                              <span className="projects-mobile-label">Credits used</span>
                              <span>{projectCost}</span>
                            </div>

                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger asChild>
                                <button
                                  type="button"
                                  className="projects-row-actions-button"
                                  aria-label={`More actions for ${project.name}`}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <DotsThree size={20} weight="bold" aria-hidden="true" />
                                </button>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Portal>
                                <DropdownMenu.Content className="projects-context-menu-content" sideOffset={5} align="end">
                                  <DropdownMenu.Item
                                    className="projects-context-menu-item projects-context-menu-item-destructive"
                                    onSelect={(event) => {
                                      event.stopPropagation();
                                      setConfirmDeleteProjectId(project.projectId);
                                    }}
                                  >
                                    Delete Project
                                  </DropdownMenu.Item>
                                </DropdownMenu.Content>
                              </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                          </div>
                        </ContextMenu.Trigger>
                        <ContextMenu.Portal>
                          <ContextMenu.Content className="projects-context-menu-content">
                            <ContextMenu.Item
                              className="projects-context-menu-item projects-context-menu-item-destructive"
                              onSelect={() => setConfirmDeleteProjectId(project.projectId)}
                            >
                              Delete Project
                            </ContextMenu.Item>
                          </ContextMenu.Content>
                        </ContextMenu.Portal>
                      </ContextMenu.Root>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="projects-pagination">
                    <div className="projects-pagination-info">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, pagination.totalCount)} of {pagination.totalCount} projects
                    </div>
                    <div className="projects-pagination-controls">
                      <button
                        className="projects-pagination-btn projects-pagination-prev"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        aria-label="Previous page"
                      >
                        ← Prev
                      </button>
                      
                      <div className="projects-pagination-pages">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="projects-pagination-ellipsis">...</span>
                          ) : (
                            <button
                              key={page}
                              className={`projects-pagination-page ${currentPage === page ? 'active' : ''}`}
                              onClick={() => goToPage(page)}
                              aria-label={`Page ${page}`}
                              aria-current={currentPage === page ? 'page' : undefined}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button
                        className="projects-pagination-btn projects-pagination-next"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={!pagination.hasNext}
                        aria-label="Next page"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Landing Page Modal for Prompt Creation */}
      {showLanding && (
        <div className="projects-landing-modal">
          <button
            className="projects-landing-modal-close"
            onClick={() => setShowLanding(false)}
            aria-label="Close landing"
          >
            ← Back to Projects
          </button>
          <LandingPage />
        </div>
      )}

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onCheckout={handleCheckout}
        usage={billingUsage}
        billingMode={billingUsage?.billingMode}
      />

      {/* Delete confirmation dialog */}
      {confirmDeleteProjectId && (
        <div className="projects-delete-overlay" onClick={() => !isDeletingProject && setConfirmDeleteProjectId(null)}>
          <div className="projects-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="projects-delete-dialog-title">Delete project?</h3>
            <p className="projects-delete-dialog-body">This action cannot be undone. All project data will be permanently removed.</p>
            <div className="projects-delete-dialog-actions">
              <button
                className="projects-delete-dialog-cancel"
                onClick={() => setConfirmDeleteProjectId(null)}
                disabled={isDeletingProject}
              >
                Cancel
              </button>
              <button
                className="projects-delete-dialog-confirm"
                onClick={() => handleDeleteProject(confirmDeleteProjectId)}
                disabled={isDeletingProject}
              >
                {isDeletingProject ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthenticatedLayout>
  );
}

export default ProjectsPage;
